import { useDateFormat } from '@eti/providers';
import { date as dateUtil } from '@eti/utils';
import { Inline, Stack } from '@etg/wings';
import { CarrierBookingNumber } from '@eti/types';
import PropTypes from 'prop-types';
import { css } from '@eti/styles';
import TimelineWrapper from '../../../../common/TimelineWrapper';
import TimelineStop from './TimelineStop';
import CheckinInfoBox from './CheckinInfoBox';

const inlineDateTimeStyles = css`
  position: relative;
  word-break: break-word;
`;

const locationStyles = css`
  word-break: break-word;
`;

const secondaryDateStyles = css`
  color: #737373;
  position: absolute;
  top: -12px;
`;

const outerStackStyles = css`
  margin-top: 12px;
`;

const stopsStackStyles = css`
  align-items: stretch;

  & > div {
    flex: 1;
  }
`;

const SegmentGroup = ({ group, inlineStyles, previousGroupArrival }) => {
  const { day, time } = useDateFormat();

  const departureDate = dateUtil.format(group.departuredAt, day);
  const departureTime = dateUtil.format(group.departuredAt, time);
  const arrivalDate = dateUtil.format(group.arrivedAt, day);
  const arrivalTime = dateUtil.format(group.arrivedAt, time);
  const previousGroupArrivalDate = dateUtil.format(previousGroupArrival, day);

  const shouldShowArrivalDate = arrivalDate !== departureDate;
  const shouldShowDepartureDate =
    previousGroupArrival && departureDate !== previousGroupArrivalDate;

  const dataTestIdPrefix = `self-checkin-${group.origin.cityCode}-${group.destination.cityCode}`;

  return (
    <Stack
      className={shouldShowDepartureDate && outerStackStyles}
      data-testid={dataTestIdPrefix}
      spacing={0}
    >
      <Inline className={inlineStyles} noWrap spacing={8}>
        <Inline align="end" alignY="center" className={inlineDateTimeStyles} spacing={0}>
          {shouldShowDepartureDate && (
            <div className={secondaryDateStyles} data-testid={`${dataTestIdPrefix}-departure-date`}>
              {departureDate}
            </div>
          )}
          <span data-testid={`${dataTestIdPrefix}-departure-time`}>{departureTime}</span>
        </Inline>
        <TimelineStop
          borderStyleAfter="solid"
          borderStyleBefore={previousGroupArrival ? '' : 'dotted'}
          data-testid={`${dataTestIdPrefix}-departure-timeline-stop`}
        />
        <Inline className={locationStyles} spacing={4}>
          <div data-testid={`${dataTestIdPrefix}-origin`}>{group.origin.cityName}</div>
          <div data-testid={`${dataTestIdPrefix}-origin-code`}>({group.origin.code})</div>
        </Inline>
      </Inline>
      <Inline className={inlineStyles} noWrap spacing={8}>
        <div />
        <Stack className={stopsStackStyles} spacing={0}>
          {group.stops.length > 0 ? (
            group.stops.map((stop) => (
              <TimelineStop
                borderStyleAfter="solid"
                borderStyleBefore="solid"
                data-testid={`${dataTestIdPrefix}-stop-${stop.cityCode}`}
                key={stop.cityName}
                tooltipText={stop.cityName}
              />
            ))
          ) : (
            <TimelineWrapper
              borderStyleAfter="solid"
              borderStyleBefore="solid"
              childSize={0}
              wrapperSize={20}
            />
          )}
        </Stack>
        <div data-testid={`${dataTestIdPrefix}-checkin-details`}>
          <CheckinInfoBox
            carrierBookingNumber={group.carrierBookingNumber}
            customerEmail={group.email}
          />
        </div>
      </Inline>
      <Inline className={inlineStyles} noWrap spacing={8}>
        <Inline align="end" alignY="center" className={inlineDateTimeStyles} spacing={0}>
          {shouldShowArrivalDate && (
            <div className={secondaryDateStyles} data-testid={`${dataTestIdPrefix}-arrival-date`}>
              {arrivalDate}
            </div>
          )}
          <span data-testid={`${dataTestIdPrefix}-arrival-time`}>{arrivalTime}</span>
        </Inline>
        <TimelineStop
          borderStyleBefore="solid"
          data-testid={`${dataTestIdPrefix}-arrival-timeline-stop`}
        />
        <Inline className={locationStyles} spacing={4}>
          <div data-testid={`${dataTestIdPrefix}-destination`}>{group.destination.cityName}</div>
          <div data-testid={`${dataTestIdPrefix}-destination-code`}>({group.destination.code})</div>
        </Inline>
      </Inline>
    </Stack>
  );
};

SegmentGroup.propTypes = {
  group: PropTypes.shape({
    arrivedAt: PropTypes.string,
    carrierBookingNumber: CarrierBookingNumber,
    departuredAt: PropTypes.string,
    destination: PropTypes.shape({
      cityCode: PropTypes.string,
      cityName: PropTypes.string,
      code: PropTypes.string,
    }),
    email: PropTypes.string,
    origin: PropTypes.shape({
      cityCode: PropTypes.string,
      cityName: PropTypes.string,
      code: PropTypes.string,
    }),
    stops: PropTypes.arrayOf(
      PropTypes.shape({
        cityCode: PropTypes.string,
        cityName: PropTypes.string,
      }),
    ),
  }),
  inlineStyles: PropTypes.string,
  previousGroupArrival: PropTypes.string,
};

export default SegmentGroup;
